import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import SupplierAutocomplete from "@/components/SupplierAutocomplete";
import { Plus, Star, Trash2, Package, Loader2, Edit, Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "", category: "", storage_location: "", batch_number: "",
  supplier: "", supplier_id: "", notes: "", image_url: "",
};

export default function Favorites() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // favorite obj or null
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const webcamRef = useRef(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchFavorites(); fetchOptions(); }, []);

  const fetchFavorites = async () => {
    try { const res = await api.get("/favorites"); setFavorites(res.data); }
    catch { toast.error("Error al cargar favoritos"); }
    finally { setLoading(false); }
  };

  const fetchOptions = async () => {
    try {
      const [c, l] = await Promise.all([api.get("/categories"), api.get("/storage-locations")]);
      setCategories(c.data || []);
      setLocations(l.data || []);
    } catch (err) { console.error("[Favorites] Failed to load options:", err); }
    finally { setOptionsLoading(false); }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (fav) => {
    setEditing(fav);
    setForm({
      name: fav.name || "", category: fav.category || "", storage_location: fav.storage_location || "",
      batch_number: fav.batch_number || "", supplier: fav.supplier || "", supplier_id: fav.supplier_id || "",
      notes: fav.notes || "", image_url: fav.image_url || "",
    });
    setShowForm(true);
  };

  const capturePhoto = useCallback(() => {
    const img = webcamRef.current?.getScreenshot();
    if (img) { setForm((prev) => ({ ...prev, image_url: img })); setShowCamera(false); }
  }, []);

  const onUploadPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm((prev) => ({ ...prev, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveForm = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.storage_location) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const res = await api.put(`/favorites/${editing.favorite_id}`, form);
        setFavorites((prev) => prev.map((f) => f.favorite_id === editing.favorite_id ? res.data : f));
        toast.success("Favorito actualizado");
      } else {
        const res = await api.post("/favorites", form);
        setFavorites((prev) => [...prev, res.data]);
        toast.success("Favorito creado");
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/favorites/${deleteId}`);
      setFavorites((prev) => prev.filter((f) => f.favorite_id !== deleteId));
      toast.success("Favorito eliminado");
    } catch { toast.error("Error al eliminar"); }
    finally { setDeleteId(null); }
  };

  const applyAsTemplate = (fav) => {
    navigate(`/products/add?favorite=${fav.favorite_id}`);
  };

  return (
    <div data-testid="favorites-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">{t("favorites.title")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t("favorites.subtitle") || "Guarda productos frecuentes como plantillas reutilizables"}</p>
        </div>
        <Button onClick={openAdd} size="sm" data-testid="add-favorite-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {t("favorites.addFavorite") || "Añadir favorito"}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : favorites.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Star className="h-12 w-12 mb-4 opacity-40" />
            <p>{t("favorites.noFavorites")}</p>
            <p className="text-sm mt-1">{t("favorites.noFavoritesHint")}</p>
            <Button variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5" onClick={openAdd} data-testid="empty-add-favorite-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("favorites.addFirst")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {favorites.map((f) => (
            <Card key={f.favorite_id} className="hover:shadow-md transition-shadow" data-testid={`favorite-card-${f.favorite_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {f.image_url ? (
                      <img src={f.image_url} alt={f.name} className="w-14 h-14 object-cover" />
                    ) : (
                      <Star className="h-6 w-6 text-amber-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{f.category}</p>
                    <p className="text-xs text-muted-foreground truncate">{f.storage_location}</p>
                    {f.supplier && <p className="text-xs text-muted-foreground truncate">· {f.supplier}</p>}
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  <Button onClick={() => applyAsTemplate(f)} size="sm" className="flex-1 h-8 text-xs" data-testid={`quick-add-${f.favorite_id}`}>
                    <Package className="h-3.5 w-3.5 mr-1" />
                    {t("favorites.quickAdd") || "Usar plantilla"}
                  </Button>
                  <Button onClick={() => openEdit(f)} size="icon" variant="outline" className="h-8 w-8" data-testid={`edit-fav-${f.favorite_id}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button onClick={() => setDeleteId(f.favorite_id)} size="icon" variant="outline" className="h-8 w-8 hover:bg-red-50 hover:text-red-600 hover:border-red-200" data-testid={`delete-fav-${f.favorite_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit modal — full Mercancía form structure */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setShowCamera(false); setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="favorite-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar favorito" : "Nuevo favorito"}</DialogTitle>
            <DialogDescription className="text-xs">
              Las plantillas reutilizan la misma estructura que Mercancía. Al usarlas, los datos se autocompletan en el formulario de producto.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveForm} className="space-y-4">
            {/* Photo */}
            <div className="space-y-2">
              <Label className="text-xs">{t("addProduct.photoTitle") || "Foto del producto"}</Label>
              {showCamera ? (
                <div className="space-y-2">
                  <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="w-full rounded-md" videoConstraints={{ facingMode: "environment" }} />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={capturePhoto} className="flex-1"><Camera className="h-3.5 w-3.5 mr-1" />Capturar</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowCamera(false)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ) : form.image_url ? (
                <div className="relative">
                  <img src={form.image_url} alt="Preview" className="w-full max-h-40 object-contain rounded-md bg-zinc-50" />
                  <Button type="button" variant="secondary" size="sm" className="absolute top-1 right-1 h-7 w-7 p-0"
                    onClick={() => setForm((p) => ({ ...p, image_url: "" }))}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setShowCamera(true)}>
                    <Camera className="h-3.5 w-3.5 mr-1" />Cámara
                  </Button>
                  <label className="flex-1">
                    <Button type="button" size="sm" variant="outline" className="w-full" asChild>
                      <span><Upload className="h-3.5 w-3.5 mr-1" />Subir</span>
                    </Button>
                    <input type="file" accept="image/*" className="hidden" onChange={onUploadPhoto} />
                  </label>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required data-testid="favorite-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("addProduct.category")} *</Label>
                <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))} disabled={optionsLoading}>
                  <SelectTrigger data-testid="favorite-category-select"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => (<SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("addProduct.storageLocation")} *</Label>
                <Select value={form.storage_location} onValueChange={(v) => setForm((p) => ({ ...p, storage_location: v }))} disabled={optionsLoading}>
                  <SelectTrigger data-testid="favorite-storage-select"><SelectValue placeholder="Ubicación" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => (<SelectItem key={l.location_id} value={l.name}>{l.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">{t("addProduct.supplier")}</Label>
                <SupplierAutocomplete
                  supplierId={form.supplier_id}
                  supplierName={form.supplier}
                  onChange={({ supplier_id, supplier }) => setForm((p) => ({ ...p, supplier_id, supplier }))}
                  data-testid="favorite-supplier-input"
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">{t("addProduct.batchNumber")}</Label>
                <Input value={form.batch_number} onChange={(e) => setForm((p) => ({ ...p, batch_number: e.target.value }))} data-testid="favorite-batch-input" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">{t("addProduct.notes")}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="favorite-notes-input" />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-favorite-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Star className="h-4 w-4 mr-2" />}
                {submitting ? t("common.saving") : (editing ? "Actualizar" : "Crear")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("favorites.deleteTitle") || "Eliminar favorito"}</AlertDialogTitle>
            <AlertDialogDescription>
              Esta plantilla se eliminará permanentemente. Los productos ya creados a partir de ella NO se verán afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90 text-white">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
